import { AnimatePresence, motion } from "motion/react";
import React from "react";
import type { Selection } from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import {
  TestMenu,
  TestMenuAllowDuplicatesSwitch,
  TestMenuOrgFilterSwitch,
  TestMenuProvider,
  TestMenuSearch,
  TestMenuTestTypeSelect,
  useTestMenuContext,
} from "@/components/TestMenu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { cn } from "@/components/ui/utils";
import type { TAccession } from "@/lib/types";
import { mutationResponse } from "@/utils/trpc";
import { useAccession } from "../AccessionProvider";
import { useDialogState } from "../detail/state";

type AddTestsDialogProps = {
  accession: TAccession;
};

export function AddTestsDialog({ accession }: AddTestsDialogProps) {
  const { testOrders, samples } = useAccession();
  const [isOpen, setIsOpen] = useDialogState("addTests");

  const { mutate: addTests } = trpc.accession.addTests.useMutation(
    mutationResponse({
      errorTitle: "Failed to add tests",
      successMessage: "Tests added",
      onSuccess: () => {
        setIsOpen(false);
      },
    }),
  );

  const [selectedPanels, setSelectedPanels] = React.useState<Set<number>>(
    new Set(),
  );
  const [selectedTests, setSelectedTests] = React.useState<Set<number>>(
    new Set(),
  );
  const [selectedSamples, setSelectedSamples] = React.useState<Set<number>>(
    new Set(samples.map((s) => s.id)),
  );

  // Update selectedSamples when samples prop changes
  React.useEffect(() => {
    setSelectedSamples(new Set(samples.map((s) => s.id)));
  }, [samples]);
  const [wizardStep, setWizardStep] = React.useState<"tests" | "samples">(
    "tests",
  );
  const [direction, setDirection] = React.useState<number>(0);

  const existingPanels = testOrders
    .map((o) => o.instruction?.panelId)
    .filter((id) => id !== null && id !== undefined);

  const existingTests = testOrders
    .map((o) => o.instruction?.testId)
    .filter((id) => id !== null && id !== undefined);

  const inferredTestType = React.useMemo(() => {
    return testOrders[0]?.testSpec.type;
  }, [testOrders[0]?.testSpec.type]);

  return (
    <TestMenuProvider
      orgId={accession.orgId}
      testType={inferredTestType ?? "SEROLOGY"}
      existingPanels={new Set(existingPanels)}
      existingTests={new Set(existingTests)}
      onSelectedPanelsChange={setSelectedPanels}
      onSelectedTestsChange={setSelectedTests}
    >
      <AddTestsWizard
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        existingPanels={existingPanels}
        existingTests={existingTests}
        samples={samples}
        selectedSamples={selectedSamples}
        setSelectedSamples={setSelectedSamples}
        wizardStep={wizardStep}
        setWizardStep={setWizardStep}
        direction={direction}
        setDirection={setDirection}
        selectedPanels={selectedPanels}
        selectedTests={selectedTests}
        onSave={(sampleIds) => {
          addTests({
            accessionId: accession.id,
            panels: Array.from(selectedPanels),
            tests: Array.from(selectedTests),
            sampleIds,
          });
        }}
      />
    </TestMenuProvider>
  );
}

type AddTestsWizardProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  existingPanels: (number | null | undefined)[];
  existingTests: (number | null | undefined)[];
  samples: Array<{
    id: number;
    displayId: string;
    tube?: { name: string } | null;
  }>;
  selectedSamples: Set<number>;
  setSelectedSamples: (samples: Set<number>) => void;
  wizardStep: "tests" | "samples";
  setWizardStep: (step: "tests" | "samples") => void;
  direction: number;
  setDirection: (direction: number) => void;
  selectedPanels: Set<number>;
  selectedTests: Set<number>;
  onSave: (sampleIds?: number[]) => void;
};

function AddTestsWizard({
  isOpen,
  setIsOpen,
  existingPanels,
  existingTests,
  samples,
  selectedSamples,
  setSelectedSamples,
  wizardStep,
  setWizardStep,
  direction,
  setDirection,
  selectedPanels,
  selectedTests,
  onSave,
}: AddTestsWizardProps) {
  const { hasPerSampleSelection } = useTestMenuContext();
  const handleNext = () => {
    // Only show sample selection for PER_SAMPLE tests with multiple samples
    const shouldShowSampleSelection =
      hasPerSampleSelection && samples.length > 1;

    if (shouldShowSampleSelection) {
      setDirection(1); // Moving forward
      setWizardStep("samples");
    } else {
      // Save directly
      // For PER_SAMPLE tests with single sample: pass the sample ID
      // For PER_ACCESSION tests: always pass undefined (all samples available)
      const sampleIds =
        hasPerSampleSelection && samples.length === 1
          ? Array.from(selectedSamples)
          : undefined;
      onSave(sampleIds);
    }
  };

  const handleSave = () => {
    const sampleIds = Array.from(selectedSamples);

    // Prevent saving with no samples selected
    if (sampleIds.length === 0) {
      return; // Could add toast notification here
    }

    onSave(sampleIds);
  };

  const handleBack = () => {
    setDirection(-1); // Moving backward
    setWizardStep("tests");
  };

  const handleClose = () => {
    // Reset all state when closing
    setWizardStep("tests");
    setDirection(0);
    setSelectedSamples(new Set(samples.map((s) => s.id))); // Reset to all samples selected
    setIsOpen(false);
  };

  const hasSelections = selectedPanels.size > 0 || selectedTests.size > 0;

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? "-100%" : "100%",
      opacity: 0,
    }),
  };

  const transition = {
    duration: 0.3,
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleClose}
      size="2xl"
      classNames={{
        modal: "sm:max-h-[min(640px,80vh)]",
      }}
    >
      <Dialog className="[&>button[slot=close]]:z-50">
        <div className="relative overflow-hidden h-[600px]">
          <AnimatePresence custom={direction}>
            {wizardStep === "tests" && (
              <motion.div
                key="tests"
                custom={direction}
                variants={slideVariants}
                initial={direction !== 0 ? "enter" : "center"}
                animate="center"
                exit="exit"
                transition={transition}
                className="absolute inset-0 flex flex-col"
              >
                <DialogHeader className="border-b">
                  <DialogTitle>Add tests</DialogTitle>
                  <div className="pt-2 flex items-center gap-4">
                    <TestMenuSearch className="flex-1" />
                    <TestMenuTestTypeSelect
                      isDisabled={
                        existingPanels.length > 0 || existingTests.length > 0
                      }
                    />
                  </div>
                </DialogHeader>

                <DialogBody className="p-0 sm:p-0 flex-1">
                  <div className="flex-1 flex flex-col h-full">
                    <TestMenu />
                  </div>
                </DialogBody>

                <DialogFooter className="border-t sm:justify-between">
                  <div className="flex items-center gap-2 flex-row-reverse">
                    <Button onPress={handleNext} isDisabled={!hasSelections}>
                      {hasPerSampleSelection && samples.length > 1
                        ? "Next"
                        : "Add tests"}
                    </Button>
                    <DialogCloseButton onPress={handleClose}>
                      Cancel
                    </DialogCloseButton>
                  </div>
                  <div className="flex items-center gap-3 flex-row-reverse">
                    <TestMenuAllowDuplicatesSwitch />
                    <TestMenuOrgFilterSwitch />
                  </div>
                </DialogFooter>
              </motion.div>
            )}
            {wizardStep === "samples" && (
              <motion.div
                key="samples"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
                className="absolute inset-0 flex flex-col"
              >
                <DialogHeader className="border-b">
                  <DialogTitle>Select samples</DialogTitle>
                  <Text className="pt-2 text-sm text-muted-foreground">
                    Choose which samples to run the selected tests on
                  </Text>
                </DialogHeader>

                <DialogBody className="p-6">
                  <SampleSelectionStep
                    samples={samples}
                    selectedSamples={selectedSamples}
                    setSelectedSamples={setSelectedSamples}
                  />
                </DialogBody>

                <DialogFooter className="border-t">
                  <div className="flex items-center gap-2 flex-row-reverse">
                    <Button
                      onPress={handleSave}
                      isDisabled={selectedSamples.size === 0}
                    >
                      Add tests
                    </Button>
                    <Button variant="outline" onPress={handleBack}>
                      Back
                    </Button>
                    <DialogCloseButton onPress={handleClose}>
                      Cancel
                    </DialogCloseButton>
                  </div>
                </DialogFooter>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Dialog>
    </Modal>
  );
}

type SampleSelectionStepProps = {
  samples: Array<{
    id: number;
    displayId: string;
    tube?: { name: string } | null;
  }>;
  selectedSamples: Set<number>;
  setSelectedSamples: (samples: Set<number>) => void;
};

function SampleSelectionStep({
  samples,
  selectedSamples,
  setSelectedSamples,
}: SampleSelectionStepProps) {
  const onSelectionChange = (keys: Selection) => {
    if (keys === "all") {
      setSelectedSamples(new Set(samples.map((s) => s.id)));
    } else {
      setSelectedSamples(new Set(Array.from(keys) as number[]));
    }
  };

  const selectAllState = React.useMemo(() => {
    if (selectedSamples.size === samples.length) {
      return "all";
    }
    if (selectedSamples.size === 0) {
      return "none";
    }
    return "some";
  }, [selectedSamples, samples]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Text className="font-medium">Available samples</Text>
        <Checkbox
          isSelected={selectAllState === "all"}
          isIndeterminate={selectAllState === "some"}
          onChange={(checked) => {
            if (checked) {
              setSelectedSamples(new Set(samples.map((s) => s.id)));
            } else {
              setSelectedSamples(new Set());
            }
          }}
        >
          Select all
        </Checkbox>
      </div>

      <GridList
        items={samples}
        aria-label="Samples"
        selectedKeys={Array.from(selectedSamples)}
        onSelectionChange={onSelectionChange}
        selectionMode="multiple"
        className={cn(
          "max-h-64 overflow-auto p-0",
          "[&>*:not(:last-child)]:relative",
          "[&>*:not(:last-child)]:after:content-['']",
          "[&>*:not(:last-child)]:after:absolute",
          "[&>*:not(:last-child)]:after:left-0",
          "[&>*:not(:last-child)]:after:right-0",
          "[&>*:not(:last-child)]:after:-bottom-px",
          "[&>*:not(:last-child)]:after:h-px",
          "[&>*:not(:last-child)]:after:bg-border",
        )}
      >
        {(item) => (
          <GridListItem
            id={item.id}
            textValue={item.displayId}
            className="px-4"
          >
            <div className="flex items-center gap-2">
              <Badge size="sm" variant="outline">
                {item.displayId}
              </Badge>
              {item.tube?.name && (
                <Text className="text-sm text-muted-foreground">
                  {item.tube.name}
                </Text>
              )}
            </div>
          </GridListItem>
        )}
      </GridList>
    </div>
  );
}
